import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { PendingUpload, UploadStatus, ContentRating } from '../types/uploads';
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  Loader2,
  Trash2,
  Link2,
  Play,
  Film,
  FileVideo,
  Upload,
  Edit2,
  Plus,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getUploadStatusLabel } from '../lib/workStatus';

interface Work {
  id: string;
  title: string;
  type: string;
}

interface EditForm {
  title: string;
  description: string;
  language: string;
  rating: ContentRating;
  runtime_minutes: string;
}

const RATINGS: ContentRating[] = ['G', 'PG', 'PG-13', 'R', 'NC-17', 'TV-Y', 'TV-G', 'TV-PG', 'TV-14', 'TV-MA'];

const STAGES = [
  { key: 'draft',   label: 'Drafts',           icon: Clock,        color: 'text-muted-foreground', statuses: ['draft'] as UploadStatus[] },
  { key: 'active',  label: 'In Pipeline',       icon: Loader2,      color: 'text-blue-500',         statuses: ['uploading', 'scanning', 'human_review', 'ingesting'] as UploadStatus[] },
  { key: 'live',    label: 'Live',              icon: CheckCircle2, color: 'text-green-500',        statuses: ['approved', 'ingested'] as UploadStatus[] },
  { key: 'issues',  label: 'Needs Attention',   icon: AlertCircle,  color: 'text-destructive',      statuses: ['rejected', 'error'] as UploadStatus[] },
] as const;

const statusLabel = (status: UploadStatus) => getUploadStatusLabel(status);

const STATUS_VARIANTS: Record<UploadStatus, string> = {
  draft: 'secondary', uploading: 'default', scanning: 'default',
  human_review: 'default', approved: 'default', rejected: 'destructive',
  ingesting: 'default', ingested: 'default', error: 'destructive',
};

function formatBytes(bytes?: number) {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

async function getVideoUrl(upload: PendingUpload): Promise<string | null> {
  if (upload.r2_key) {
    const { data, error } = await supabase.functions.invoke('r2-signed-url', {
      body: { pending_upload_id: upload.id, purpose: 'preview' },
    });
    if (error || !data?.signed_url) return null;
    return data.signed_url;
  }
  if (upload.storage_path) {
    const { data } = supabase.storage.from('uploads-temp').getPublicUrl(upload.storage_path);
    return data?.publicUrl ?? null;
  }
  return null;
}

export default function AssetManagement() {
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const [works, setWorks] = useState<Work[]>([]);
  const [workMap, setWorkMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState<string>('all');

  // Preview
  const [previewUpload, setPreviewUpload] = useState<PendingUpload | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Link to title
  const [linkUpload, setLinkUpload] = useState<PendingUpload | null>(null);
  const [linking, setLinking] = useState(false);

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Edit metadata
  const [editUpload, setEditUpload] = useState<PendingUpload | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ title: '', description: '', language: '', rating: 'G', runtime_minutes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (publisher?.id) fetchData();
  }, [publisher?.id]);

  const fetchData = async () => {
    if (!publisher?.id) return;
    setLoading(true);
    try {
      const [uploadsRes, worksRes] = await Promise.all([
        supabase
          .from('pending_uploads')
          .select('*')
          .eq('publisher_id', publisher.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('works')
          .select('id, title, type')
          .eq('publisher_id', publisher.id)
          .in('type', ['movie', 'short_film', 'documentary', 'tv_show'])
          .order('title'),
      ]);

      if (uploadsRes.error) throw uploadsRes.error;
      const rows = (uploadsRes.data || []) as unknown as PendingUpload[];
      setUploads(rows);

      const worksData = (worksRes.data || []) as Work[];
      setWorks(worksData);
      const map: Record<string, string> = {};
      for (const w of worksData) map[w.id] = w.title;
      setWorkMap(map);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const upload = uploads.find(u => u.id === deleteId);
      if (upload?.storage_path && !upload?.r2_key) {
        await supabase.storage.from('uploads-temp').remove([upload.storage_path]);
      }
      const { error } = await supabase.from('pending_uploads').delete().eq('id', deleteId);
      if (error) throw error;
      setUploads(prev => prev.filter(u => u.id !== deleteId));
      toast.success('Asset deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const handleLinkToWork = async (workId: string) => {
    if (!linkUpload) return;
    setLinking(true);
    try {
      const { error } = await supabase
        .from('pending_uploads')
        .update({ work_id: workId })
        .eq('id', linkUpload.id);
      if (error) throw error;
      setUploads(prev => prev.map(u => u.id === linkUpload.id ? { ...u, work_id: workId } : u));
      toast.success(`Linked to "${workMap[workId]}"`);
      setLinkUpload(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to link asset');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (uploadId: string) => {
    try {
      const { error } = await supabase
        .from('pending_uploads')
        .update({ work_id: null })
        .eq('id', uploadId);
      if (error) throw error;
      setUploads(prev => prev.map(u => u.id === uploadId ? { ...u, work_id: null } : u));
      toast.success('Asset unlinked');
    } catch (err: any) {
      toast.error(err.message || 'Failed to unlink');
    }
  };

  const handlePreview = async (upload: PendingUpload) => {
    setPreviewUpload(upload);
    setPreviewUrl(null);
    const url = await getVideoUrl(upload);
    setPreviewUrl(url);
  };

  const openEdit = (upload: PendingUpload) => {
    setEditUpload(upload);
    setEditForm({
      title: upload.metadata.title ?? '',
      description: upload.metadata.description ?? '',
      language: upload.metadata.language ?? '',
      rating: upload.metadata.rating ?? 'G',
      runtime_minutes: upload.metadata.runtime_claimed ? String(Math.round(upload.metadata.runtime_claimed / 60)) : '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editUpload) return;
    if (!editForm.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const runtimeSeconds = editForm.runtime_minutes ? parseInt(editForm.runtime_minutes) * 60 : undefined;
      const updatedMetadata = {
        ...editUpload.metadata,
        title: editForm.title.trim(),
        description: editForm.description.trim() || undefined,
        language: editForm.language.trim() || editUpload.metadata.language,
        rating: editForm.rating,
        ...(runtimeSeconds !== undefined ? { runtime_claimed: runtimeSeconds } : {}),
      };
      const { error } = await supabase
        .from('pending_uploads')
        .update({ metadata: updatedMetadata })
        .eq('id', editUpload.id);
      if (error) throw error;
      setUploads(prev => prev.map(u => u.id === editUpload.id ? { ...u, metadata: updatedMetadata } : u));
      toast.success('Metadata updated');
      setEditUpload(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update metadata');
    } finally {
      setSaving(false);
    }
  };

  const getStageUploads = (statuses: readonly UploadStatus[]) =>
    uploads.filter(u => statuses.includes(u.upload_status));

  const visibleUploads = activeStage === 'all'
    ? uploads
    : STAGES.find(s => s.key === activeStage)?.statuses.flatMap(
        status => uploads.filter(u => u.upload_status === status)
      ) ?? [];

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading assets...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Asset Pipeline</h1>
          <p className="text-muted-foreground mt-1">Preview, review, and link uploaded video files to your titles</p>
        </div>
        <Button onClick={() => navigate('/content/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Upload
        </Button>
      </div>

      {/* Stage filter */}
      <div className="flex flex-wrap gap-2">
        <Button variant={activeStage === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setActiveStage('all')}>
          All ({uploads.length})
        </Button>
        {STAGES.map(stage => {
          const count = getStageUploads(stage.statuses).length;
          return (
            <Button
              key={stage.key}
              variant={activeStage === stage.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveStage(stage.key)}
            >
              <stage.icon className={`h-3.5 w-3.5 mr-1.5 ${stage.color} ${stage.key === 'active' ? 'animate-spin' : ''}`} />
              {stage.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Upload list */}
      {uploads.length === 0 ? (
        <Card className="partner-card border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <FileVideo className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">No assets yet</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Upload a video from a movie or series detail page to see it here.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/content/movies')}>
              <Film className="h-4 w-4 mr-2" />
              Go to Movies
            </Button>
          </CardContent>
        </Card>
      ) : visibleUploads.length === 0 ? (
        <Card className="partner-card">
          <CardContent className="py-8 text-center text-muted-foreground text-sm">No assets in this stage.</CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visibleUploads.map(upload => {
            const canDelete = upload.upload_status === 'draft' || upload.upload_status === 'error';
            const canPreview = !!upload.storage_path || !!upload.r2_key;
            const isPaidDraft = upload.upload_status === 'draft'
              && !!upload.upload_fee_id
              && !upload.r2_key;
            const isActive = ['uploading', 'scanning', 'human_review', 'ingesting'].includes(upload.upload_status);
            const isLive = upload.upload_status === 'ingested';
            const linkedTitle = upload.work_id ? workMap[upload.work_id] : null;
            const filename = upload.storage_path?.split('/').pop();

            return (
              <Card key={upload.id} className="partner-card">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Status dot */}
                    <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                      isLive ? 'bg-green-500' :
                      isActive ? 'bg-blue-500 animate-pulse' :
                      upload.upload_status === 'draft' ? 'bg-muted-foreground/50' :
                      'bg-destructive'
                    }`} />

                    <div className="flex-1 min-w-0 space-y-1.5">
                      {/* Title row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">
                          {upload.metadata?.title || filename || 'Untitled asset'}
                        </p>
                        <Badge variant={STATUS_VARIANTS[upload.upload_status] as any} className="text-xs shrink-0">
                          {statusLabel(upload.upload_status)}
                        </Badge>
                        {!upload.work_id && (
                          <Badge variant="outline" className="text-xs shrink-0 text-yellow-500 border-yellow-500/50">
                            Unlinked
                          </Badge>
                        )}
                        {upload.upload_status === 'draft' && upload.upload_fee_id && !upload.r2_key && (
                          <Badge variant="outline" className="text-xs text-green-500 border-green-500/50 shrink-0">
                            Fee paid — ready for upload
                          </Badge>
                        )}
                        {upload.upload_status === 'draft' && !upload.upload_fee_id && (
                          <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/50 shrink-0">
                            Needs payment
                          </Badge>
                        )}
                      </div>

                      {/* Meta row */}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span className="capitalize">{upload.content_type?.replace(/_/g, ' ')}</span>
                        {formatBytes(upload.file_size) && <span>{formatBytes(upload.file_size)}</span>}
                        {linkedTitle ? (
                          <span
                            className="text-primary cursor-pointer hover:underline"
                            onClick={() => navigate(`/content/movies/${upload.work_id}`)}
                          >
                            → {linkedTitle}
                          </span>
                        ) : null}
                        <span>{formatDistanceToNow(new Date(upload.created_at), { addSuffix: true })}</span>
                      </div>

                      {/* Rejection reason */}
                      {upload.upload_status === 'rejected' && upload.human_review?.notes && (
                        <p className="text-xs text-destructive">
                          Rejected: {upload.human_review.notes}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                      {isPaidDraft && (
                        <Button size="sm" variant="default" onClick={() => navigate(`/content/upload/${upload.id}?fee_paid=true`)}>
                          <Upload className="h-3.5 w-3.5 mr-1" />
                          Upload File
                        </Button>
                      )}
                      {canPreview && (
                        <Button size="sm" variant="outline" onClick={() => handlePreview(upload)}>
                          <Play className="h-3.5 w-3.5 mr-1" />
                          Preview
                        </Button>
                      )}
                      {upload.work_id ? (
                        <Button size="sm" variant="ghost" onClick={() => handleUnlink(upload.id)} title="Unlink from title">
                          <Link2 className="h-3.5 w-3.5 mr-1 opacity-50" />
                          Unlink
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setLinkUpload(upload)}>
                          <Link2 className="h-3.5 w-3.5 mr-1" />
                          Link to Title
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(upload)}
                          title="Edit metadata"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(upload.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stage summary */}
      {uploads.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          {STAGES.map(stage => {
            const count = getStageUploads(stage.statuses).length;
            return (
              <Card
                key={stage.key}
                className={`partner-card cursor-pointer transition-colors hover:border-primary/40 ${activeStage === stage.key ? 'border-primary' : ''}`}
                onClick={() => setActiveStage(activeStage === stage.key ? 'all' : stage.key)}
              >
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{stage.label}</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className={`text-2xl font-bold ${stage.color}`}>{count}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Preview dialog */}
      <Dialog open={!!previewUpload} onOpenChange={open => { if (!open) { setPreviewUpload(null); setPreviewUrl(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewUpload?.metadata?.title || previewUpload?.storage_path?.split('/').pop() || 'Asset Preview'}</DialogTitle>
            <DialogDescription className="flex items-center gap-3 text-xs">
              <span className="capitalize">{previewUpload?.content_type?.replace(/_/g, ' ')}</span>
              {formatBytes(previewUpload?.file_size) && <span>{formatBytes(previewUpload?.file_size)}</span>}
              {previewUpload && (
                <Badge variant={STATUS_VARIANTS[previewUpload.upload_status] as any} className="text-xs">
                  {statusLabel(previewUpload.upload_status)}
                </Badge>
              )}
              {previewUpload?.work_id && workMap[previewUpload.work_id] && (
                <span className="text-primary">→ {workMap[previewUpload.work_id]}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {previewUrl ? (
            <div className="rounded-lg overflow-hidden bg-black">
              <video
                key={previewUpload?.id}
                src={previewUrl}
                controls
                className="w-full max-h-[60vh]"
                preload="metadata"
              >
                Your browser does not support video playback.
              </video>
            </div>
          ) : previewUpload && (!!previewUpload.storage_path || !!previewUpload.r2_key) ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading preview...
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <FileVideo className="h-8 w-8 mr-2" />
              No video file available yet.
            </div>
          )}

          <div className="flex justify-between items-center pt-1">
            <div className="text-xs text-muted-foreground space-y-0.5">
              {previewUpload?.storage_path && (
                <p className="font-mono truncate max-w-xs">{previewUpload.storage_path.split('/').pop()}</p>
              )}
            </div>
            <div className="flex gap-2">
              {!previewUpload?.work_id && (
                <Button size="sm" variant="outline" onClick={() => { setLinkUpload(previewUpload); setPreviewUpload(null); }}>
                  <Link2 className="h-3.5 w-3.5 mr-1" />
                  Link to Title
                </Button>
              )}
              {previewUpload?.work_id && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/content/movies/${previewUpload.work_id}`)}>
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  View Title
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link to title dialog */}
      <Dialog open={!!linkUpload} onOpenChange={open => !open && setLinkUpload(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link Asset to Title</DialogTitle>
            <DialogDescription>
              Choose which movie, short film, or documentary this video belongs to.
            </DialogDescription>
          </DialogHeader>
          {works.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No titles found. Create a movie first.
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {works.map(work => (
                <button
                  key={work.id}
                  disabled={linking}
                  onClick={() => handleLinkToWork(work.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/60 hover:bg-muted/30 transition-colors text-left disabled:opacity-50"
                >
                  <Film className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{work.title}</p>
                    <p className="text-xs text-muted-foreground capitalize">{work.type.replace('_', ' ')}</p>
                  </div>
                  {linking && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the upload record and the file from storage. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit metadata dialog */}
      <Dialog open={!!editUpload} onOpenChange={open => !open && setEditUpload(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Metadata</DialogTitle>
            <DialogDescription>Update the metadata for this upload.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Content title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Description"
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Input
                  value={editForm.language}
                  onChange={e => setEditForm(f => ({ ...f, language: e.target.value }))}
                  placeholder="en"
                />
              </div>
              <div className="space-y-2">
                <Label>Rating</Label>
                <Select value={editForm.rating} onValueChange={v => setEditForm(f => ({ ...f, rating: v as ContentRating }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATINGS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Runtime (minutes)</Label>
              <Input
                type="number"
                min="1"
                value={editForm.runtime_minutes}
                onChange={e => setEditForm(f => ({ ...f, runtime_minutes: e.target.value }))}
                placeholder="90"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUpload(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
